import { useQuery } from "@tanstack/react-query";
import { ExclamationTriangleIcon, UserCircleIcon } from "@heroicons/react/24/outline";

import { getMyActions } from "../api/auditApi";

// ============================================================================
// Helpers
// ============================================================================

const fmtDate = (d: string) =>
    new Date(d).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" });

const statusColors: Record<string, string> = {
    SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    FAILURE: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    PARTIAL: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    DENIED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

// ============================================================================
// Component
// ============================================================================

export const MyActionsRoute = () => {
    const { data, isLoading, error } = useQuery({
        queryKey: ["audit-my-actions"],
        queryFn: getMyActions,
    });

    const items = data?.data ?? [];

    // ────────────────────────────────────────────────────────────────────────
    // Render
    // ────────────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center gap-2">
                <UserCircleIcon className="h-5 w-5 text-primary-500" />
                <h3 className="font-semibold text-slate-900 dark:text-white">
                    My Recent Actions
                </h3>
                <span className="text-xs text-slate-400">(Last 50)</span>
            </div>

            {/* Content */}
            <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                {isLoading ? (
                    <div className="flex justify-center py-16">
                        <div className="h-7 w-7 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                    </div>
                ) : error ? (
                    <div className="px-5 py-10 text-center text-sm text-rose-500">
                        <ExclamationTriangleIcon className="mx-auto mb-2 h-7 w-7" />
                        Failed to load your actions
                    </div>
                ) : !items.length ? (
                    <p className="px-5 py-10 text-center text-sm text-slate-400">
                        You have no recorded actions yet
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-xs text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                                <tr>
                                    <th className="px-4 py-3 text-left">Action</th>
                                    <th className="px-4 py-3 text-left">Category</th>
                                    <th className="px-4 py-3 text-left">Resource</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                    <th className="px-4 py-3 text-left">Time</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {items.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900 dark:text-white">
                                            {item.action}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            {item.actionCategory}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            <span className="font-medium">{item.resourceType}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[item.status] ??
                                                    statusColors.SUCCESS
                                                    }`}
                                            >
                                                {item.status}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                            {fmtDate(item.performedAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
